import { useAnswerMfa } from '@myrentalmate/api-client';
import { Alert, Button, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

/** The second step of signing in, after a right password or sign-in link. */
export function TwoFactorChallenge({
  challenge,
  onBack,
  onSignedIn,
}: {
  challenge: string;
  onBack?: () => void;
  onSignedIn?: () => void;
}) {
  const [code, setCode] = useState('');
  const queryClient = useQueryClient();
  const answer = useAnswerMfa({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries();
        onSignedIn?.();
      },
      onError: () => setCode(''),
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    answer.mutate({ data: { challenge, code } });
  }

  return (
    <Stack component="form" spacing={2} onSubmit={submit}>
      <Typography color="text.secondary">
        Enter the 6-digit code from your authenticator app, or a backup code.
      </Typography>
      {answer.isError && (
        <Alert severity="error">
          That code isn't right, or it's been too long. Try again, or start over.
        </Alert>
      )}
      <TextField
        label="Code"
        required
        autoFocus
        autoComplete="one-time-code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
      />
      <Button type="submit" variant="contained" size="large" disabled={answer.isPending}>
        {answer.isPending ? 'Checking…' : 'Verify'}
      </Button>
      {onBack && (
        <Button variant="text" onClick={onBack}>
          Start over
        </Button>
      )}
    </Stack>
  );
}
